import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Lock, User, Save, X, LogOut } from 'lucide-react';
import { theme } from '../styles/theme';
import { getPrices, savePrices, signInAdmin, signOutAdmin, onAuthChange } from '../services/pricingStore';

// Firebase Auth kullanılmaktadır; eski yerel kimlik doğrulama kaldırıldı

const AdminOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: ${theme.spacing.lg};
`;

const AdminContainer = styled.div`
  background: ${theme.colors.glass.background};
  backdrop-filter: ${theme.colors.glass.backdrop};
  border: 1px solid ${theme.colors.glass.border};
  border-radius: 20px;
  padding: ${theme.spacing['2xl']};
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: ${theme.colors.glass.shadow};
  position: relative;
`;

const AdminHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  padding-bottom: ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.border};
`;

const AdminTitle = styled.h2`
  font-size: ${theme.fontSizes['2xl']};
  color: ${theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  padding: ${theme.spacing.sm};
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: ${theme.colors.text.primary};
  }
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  padding: ${theme.spacing.md};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.md};
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.1);
  }
  
  &::placeholder {
    color: ${theme.colors.text.light};
  }
`;

const LoginButton = styled.button`
  background: ${theme.colors.gradient.primary};
  color: white;
  border: none;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: 12px;
  font-size: ${theme.fontSizes.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(217, 119, 6, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PriceEditor = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const CategorySection = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const CategoryTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
  padding-bottom: ${theme.spacing.sm};
  border-bottom: 1px solid ${theme.colors.border};
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.sm};
  border-radius: 8px;
  transition: background 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const ItemInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ItemNameText = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
  font-weight: 600;
`;

const ItemDescText = styled.span`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.secondary};
`;

const PriceInput = styled.input`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  padding: ${theme.spacing.sm};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  width: 80px;
  text-align: center;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const SaveButton = styled.button`
  background: ${theme.colors.gradient.accent};
  color: white;
  border: none;
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  border-radius: 12px;
  font-size: ${theme.fontSizes.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.xl};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(245, 158, 11, 0.4);
  }
`;

const ErrorMessage = styled.div`
  background: rgba(220, 38, 38, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.3);
  color: ${theme.colors.error};
  padding: ${theme.spacing.md};
  border-radius: 8px;
  font-size: ${theme.fontSizes.sm};
  margin-top: ${theme.spacing.md};
`;

const SuccessMessage = styled.div`
  background: rgba(5, 150, 105, 0.1);
  border: 1px solid rgba(5, 150, 105, 0.3);
  color: ${theme.colors.success};
  padding: ${theme.spacing.md};
  border-radius: 8px;
  font-size: ${theme.fontSizes.sm};
  margin-top: ${theme.spacing.md};
`;

export const AdminPanel = ({ isVisible, onClose, menuData, onPriceUpdate }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editedPrices, setEditedPrices] = useState({});

  // Panel açıldığında fiyatları yükle
  useEffect(() => {
    if (isVisible && isAuthenticated) {
      loadPrices();
    }
  }, [isVisible, isAuthenticated]);

  // Global fiyatları yükle (Firebase varsa oradan, yoksa local)
  const loadPrices = async () => {
    try {
      const prices = await getPrices();
      setEditedPrices(prices || {});
    } catch (e) {
      console.error('Fiyat verisi yüklenemedi:', e);
    }
  };

  // Auth state izle
  useEffect(() => {
    const unsub = onAuthChange((user) => {
      setIsAuthenticated(!!user);
    });
    return () => { if (typeof unsub === 'function') unsub(); };
  }, []);

  // Giriş işlemi (Firebase Auth)
  const handleLogin = (e) => {
    e.preventDefault();
    setError('');
    signInAdmin(loginData.email, loginData.password)
      .then(() => setSuccess('Başarıyla giriş yapıldı!'))
      .catch((err) => setError(err?.message || 'E-posta veya şifre hatalı!'));
  };

  // Fiyat değişikliği
  const handlePriceChange = (itemId, newPrice) => {
    // Boş string veya sadece boşluk karakterleri kontrolü
    if (newPrice.trim() === '') {
      setEditedPrices(prev => {
        const updated = { ...prev };
        delete updated[itemId]; // Boş değer için item'ı kaldır
        return updated;
      });
      return;
    }

    const price = parseFloat(newPrice);
    if (!isNaN(price) && price >= 0) {
      setEditedPrices(prev => ({
        ...prev,
        [itemId]: price
      }));
    }
  };

  // Fiyatları kaydetme (global)
  const handleSave = async () => {
    try {
      await savePrices(editedPrices);
      onPriceUpdate(editedPrices);
      setSuccess('Fiyatlar başarıyla güncellendi!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError('Fiyatlar kaydedilemedi!');
    }
  };

  // Panel kapanırken reset
  const handleClose = () => {
    setIsAuthenticated(false);
    setLoginData({ email: '', password: '' });
    setError('');
    setSuccess('');
    
    // URL'i temizle
    window.history.replaceState({}, '', window.location.pathname);
    
    onClose();
  };

  if (!isVisible) return null;

  return (
    <AdminOverlay>
      <AdminContainer>
        <AdminHeader>
          <AdminTitle>
            <Lock size={24} />
            Admin Panel
          </AdminTitle>
          <CloseButton onClick={handleClose}>
            <X size={20} />
          </CloseButton>
        </AdminHeader>

        {!isAuthenticated ? (
          <LoginForm onSubmit={handleLogin}>
            <InputGroup>
              <Label>
                <User size={16} />
                E-posta
              </Label>
              <Input
                type="email"
                placeholder="E-posta adresinizi girin"
                value={loginData.email}
                onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </InputGroup>

            <InputGroup>
              <Label>
                <Lock size={16} />
                Şifre
              </Label>
              <Input
                type="password"
                placeholder="Şifrenizi girin"
                value={loginData.password}
                onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                required
              />
            </InputGroup>

            <LoginButton type="submit">
              <Lock size={16} />
              Giriş Yap
            </LoginButton>

            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}
          </LoginForm>
        ) : (
          <PriceEditor>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <LoginButton onClick={() => signOutAdmin()} type="button" style={{ background: 'transparent' }}>
                <LogOut size={16} />
                Çıkış yap
              </LoginButton>
            </div>
            {menuData.categories.map(category => (
              <CategorySection key={category.id}>
                <CategoryTitle>{category.name}</CategoryTitle>
                {category.items.map(item => (
                  <ItemRow key={item.id}>
                    <ItemInfo>
                      <ItemNameText>{item.name}</ItemNameText>
                      <ItemDescText>{item.description}</ItemDescText>
                    </ItemInfo>
                    <PriceInput
                      type="number"
                      step="0.01"
                      min="0"
                      value={editedPrices[item.id] !== undefined ? editedPrices[item.id] : ''}
                      onChange={(e) => handlePriceChange(item.id, e.target.value)}
                      placeholder={item.price.toString()}
                    />
                  </ItemRow>
                ))}
              </CategorySection>
            ))}

            <SaveButton onClick={handleSave}>
              <Save size={16} />
              Fiyatları Kaydet
            </SaveButton>

            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}
          </PriceEditor>
        )}
      </AdminContainer>
    </AdminOverlay>
  );
};
